import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

interface AuditInfo {
  createdByName?: string;
  createdAt?: string | Date;
  updatedBy?: number;
  updatedByName?: string;
  updatedAt?: string | Date;
}

@Component({
  selector: 'app-audit-badge',
  templateUrl: './audit-badge.component.html',
  styleUrls: ['./audit-badge.component.scss']
})
export class AuditBadgeComponent {
  @Input() audit: any = null;
  @Input() summary = '';
  @Input() loading = false;
  @Input() lazy = false;
  @Output() auditOpen = new EventEmitter<void>();

  get auditInfo(): AuditInfo | null {
    const raw = this.audit?.audit ? this.audit.audit : this.audit;
    if (!raw) {
      return null;
    }

    const createdAt = this.pick(raw, ['createdAt', 'createdDate', 'createdOn', 'createdDatetime', 'createdTs']);
    const updatedAt = this.pick(raw, ['updatedAt', 'updatedDate', 'updatedOn', 'updatedDatetime', 'updatedTs']);

    return {
      createdByName: this.pick(raw, ['createdByName', 'createdUserName', 'createdBy']) || raw?.createdByUser?.name || raw?.createdByUser?.fullName,
      createdAt,
      updatedBy: Number(this.pick(raw, ['updatedBy', 'lastUpdatedBy']) || 0),
      updatedByName: this.pick(raw, ['updatedByName', 'updatedUserName']) || raw?.updatedByUser?.name || raw?.updatedByUser?.fullName,
      updatedAt
    };
  }

  get label(): string {
    if (this.summary) {
      return this.summary;
    }
    if (!this.auditInfo?.createdAt) {
      return this.loading ? 'Loading...' : 'View audit';
    }
    const updated = this.hasUpdatedAudit();
    const sourceDate = updated ? this.auditInfo?.updatedAt : this.auditInfo?.createdAt;
    const prefix = updated ? 'Updated' : 'Added';
    const relative = this.getRelativeTime(sourceDate);
    return relative === 'just now' ? `${prefix} just now` : `${prefix} ${relative} ago`;
  }

  get showButton(): boolean {
    return this.lazy || !!this.summary || !!this.auditInfo?.createdAt;
  }

  onClick(popover: NgbPopover): void {
    if (this.lazy && !this.auditInfo?.createdAt && !this.loading) {
      this.auditOpen.emit();
    }

    if (popover.isOpen()) {
      popover.close();
      return;
    }

    popover.open();
  }

  hasUpdatedAudit(): boolean {
    const info = this.auditInfo;
    if (!info) {
      return false;
    }
    if (Number(info.updatedBy) > 0) {
      return true;
    }

    const createdAt = this.toDate(info.createdAt);
    const updatedAt = this.toDate(info.updatedAt);
    return !!(createdAt && updatedAt && createdAt.getTime() !== updatedAt.getTime());
  }

  getRelativeTime(value?: string | Date): string {
    const date = this.toDate(value);
    if (!date) {
      return 'recently';
    }

    const diffMs = Math.max(0, Date.now() - date.getTime());
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const month = 30 * day;
    const year = 365 * day;

    if (diffMs < minute) {
      return 'just now';
    }

    if (diffMs < hour) {
      const mins = Math.floor(diffMs / minute);
      return `${mins} min${mins > 1 ? 's' : ''}`;
    }

    if (diffMs < day) {
      const hrs = Math.floor(diffMs / hour);
      return `${hrs} hr${hrs > 1 ? 's' : ''}`;
    }

    if (diffMs < month) {
      const days = Math.floor(diffMs / day);
      return `${days} day${days > 1 ? 's' : ''}`;
    }

    if (diffMs < year) {
      const months = Math.floor(diffMs / month);
      return `${months} mo${months > 1 ? 's' : ''}`;
    }

    const years = Math.floor(diffMs / year);
    return `${years} yr${years > 1 ? 's' : ''}`;
  }

  private toDate(value?: string | Date): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private pick(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return null;
  }
}
